import { ListChecks, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { normalizeSearch } from '@/lib/search';

import { MetaCard } from '../components/meta-card';
import { MetasFiltros } from '../components/metas-filtros';
import { useMetaUsuarios, useMetas } from '../hooks/use-metas';
import type { GoalsFilters } from '../metas.types';

/**
 * Listado de metas (`/metas/listado`).
 *
 * Los filtros de estado, tipo y responsable los resuelve el backend; la búsqueda
 * por nombre se aplica en el cliente para no repetir la consulta en cada tecla.
 */
export function MetasListadoPage() {
  useDocumentTitle('Metas · Listado');

  const [filtros, setFiltros] = useState<GoalsFilters>({});
  const [busqueda, setBusqueda] = useState('');

  const { data, isPending, isError, refetch, isRefetching } = useMetas(filtros);
  const usuarios = useMetaUsuarios();

  const metas = useMemo(() => data ?? [], [data]);

  const visibles = useMemo(() => {
    const termino = normalizeSearch(busqueda);
    if (termino.length < 2) return metas;

    return metas.filter((goal) =>
      normalizeSearch([goal.goal_name, goal.fuel_grade_name, goal.first_name, goal.last_name].join(' ')).includes(termino),
    );
  }, [metas, busqueda]);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Seguimiento</p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <ListChecks className="size-6 text-brand-500" aria-hidden="true" />
            Listado de metas
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            Todas las metas del sistema con sus filtros de estado, tipo y responsable.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/metas"
            className="inline-flex items-center gap-2 rounded-control border border-ink-200 bg-white px-3 py-2 text-xs font-semibold text-ink-700 transition-colors hover:border-brand-300 hover:text-brand-700"
          >
            Panel de metas
          </Link>
          <Link
            to="/metas/nueva"
            className="inline-flex items-center gap-2 rounded-control bg-brand-600 px-3.5 py-2 text-xs font-semibold text-white transition-colors hover:bg-brand-700"
          >
            <Plus className="size-3.5" aria-hidden="true" />
            Nueva meta
          </Link>
        </div>
      </header>

      {isError ? (
        <Alert variant="error" title="No se pudo cargar el listado">
          <div className="flex flex-wrap items-center gap-3">
            <span>Verifica la conexión con el servidor e inténtalo nuevamente.</span>
            <Button size="sm" variant="outline" onClick={() => void refetch()} loading={isRefetching}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      <MetasFiltros
        filtros={filtros}
        onFiltrosChange={setFiltros}
        busqueda={busqueda}
        onBusquedaChange={setBusqueda}
        usuarios={usuarios.data ?? []}
        total={visibles.length}
      />

      {isPending ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-44 rounded-card" />
          ))}
        </div>
      ) : visibles.length === 0 ? (
        <div className="rounded-card border border-dashed border-ink-200 bg-white/70 px-6 py-14 text-center">
          <ListChecks className="mx-auto size-6 text-ink-300" aria-hidden="true" />
          <h2 className="mt-3 text-base font-semibold text-ink-800">Sin metas para mostrar</h2>
          <p className="mx-auto mt-1.5 max-w-md text-sm leading-relaxed text-ink-500">
            {metas.length === 0
              ? 'Todavía no hay metas registradas en el sistema.'
              : 'Ninguna meta coincide con los filtros aplicados.'}
          </p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {visibles.map((goal) => (
            <MetaCard key={goal.id} goal={goal} />
          ))}
        </div>
      )}
    </div>
  );
}
